import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Cluster } from '@googlemaps/markerclusterer';

import {
    GoogleMap,
    GoogleMapsModule,
    MapAdvancedMarker,
    MapAnchorPoint,
    MapInfoWindow,
    MapMarkerClusterer
} from '@angular/google-maps';
import { isEqual } from 'lodash-es';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { Coordinate } from '../../../core/common/entities/position/coordinate.class';
import { BaseNestedFieldDefinition } from '../../../core/field-definitions/entities/field-definition.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { DialogConfig } from '../../../dynamic-dialog/entities/dialog-config.entity';
import { ShDynamicDialogService } from '../../../dynamic-dialog/services/dynamic-dialog.service';
import { ShActionGroupComponent } from '../../../elements/components/action-group/action-group.component';
import {
    ActionGroup,
    ActionGroupModifier
} from '../../../elements/components/action-group/entities/action-group.entity';
import { ActionItem, ActionItemModifier } from '../../../elements/components/action-item/entities/action-item.entity';
import { NavigationLinkDirective } from '../../../elements/directives/navigation-link/navigation-link.directive';
import { FieldReadHelper } from '../../../profile-form/helpers/fields/field-read.helper';
import { ShClusterInfoDialogComponent } from '../../containers/cluster-info-dialog/cluster-info-dialog.component';
import { Marker } from '../../entities/marker.entity';
import { ShGoogleMapsService } from '../../services/google-maps/google-maps.service';

@Component({
    selector: 'sh-records-map',
    templateUrl: './records-map.component.html',
    styleUrls: ['./records-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [],
    standalone: true,
    imports: [
        NgTemplateOutlet,
        NavigationLinkDirective,
        ShActionGroupComponent,
        AsyncPipe,
        GoogleMap,
        MapAdvancedMarker,
        MapInfoWindow,
        MapMarkerClusterer,
        GoogleMapsModule
    ]
})
export class ShRecordsMapComponent extends BaseContainer implements OnInit, AfterViewInit, OnDestroy {
    /**
     * NOTE: To use this component you need to add following script to your index.html:
     * <script src="https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js" integrity="sha384-EVwzhfwoZgEjEQ2ffmvGXq5cOevVMtRZTog22siWEV3jCmD0BahdPQHx8y/VIhbi" crossorigin="anonymous"></script>
     */

    public maxWidth = 425;

    public minClusterSize = 2;

    public MIN_ZOOM = 2;
    public MAX_ZOOM = 18;
    public DEFAULT_ZOOM = 15;

    public markers$: Observable<Marker<PartialRecord>[]>;
    public infoContent: null | Marker<PartialRecord> = null;

    private readonly clusters$: ReplaySubject<Cluster[]> = new ReplaySubject<Cluster[]>();
    private readonly records$: ReplaySubject<PartialRecord[]> = new ReplaySubject<PartialRecord[]>();
    private readonly actionGroups$: BehaviorSubject<ActionGroup[]> = new BehaviorSubject<ActionGroup[]>([]);

    private googleMap: GoogleMap;

    private readonly defaultZoom$: Observable<number>;

    public readonly googleMapsOptions$: Observable<google.maps.MapOptions>;
    public readonly apiLoaded$: Observable<boolean>;
    public readonly googleMapIsReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @ViewChild(MapInfoWindow, { static: false }) info: MapInfoWindow;
    @ViewChild('googleMap') set _googleMap(googleMap: ElementRef) {
        if (googleMap && googleMap instanceof GoogleMap) {
            this.googleMap = googleMap;
            this.googleMapIsReady$.next(true);
        }
    }

    @Input() set records(records: PartialRecord[]) {
        if (records) {
            this.records$.next(records);
        }
    }

    @Input() set actionGroups(actionGroups: ActionGroup[]) {
        if (actionGroups) {
            this.actionGroups$.next(actionGroups);
        }
    }

    @Input() positionFieldDefinition?: BaseNestedFieldDefinition;

    @Input() autoFitBounds = true;

    @Input() centerCoordinate: Coordinate = new Coordinate(50.85045, 4.34878);

    @Output() boundsChange: EventEmitter<google.maps.LatLngBounds> = new EventEmitter<google.maps.LatLngBounds>();

    private readonly googleMapsSandBox: ShGoogleMapsService = inject(ShGoogleMapsService);

    constructor(
        private readonly fieldReadHelper: FieldReadHelper,
        private readonly dialogService: ShDynamicDialogService
    ) {
        super();

        this.defaultZoom$ = this.googleMapsSandBox.getClientZoomLevel();

        this.googleMapsOptions$ = this.defaultZoom$.pipe(
            take(1),
            map(zoomLevel => ({
                zoom: zoomLevel,
                center: this.centerCoordinate.toLatLing(),
                mapTypeId: 'roadmap',
                clickableIcons: false,
                fullscreenControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                zoomControl: true,
                restriction: {
                    latLngBounds: {
                        east: 180,
                        north: 85,
                        south: -85,
                        west: -180
                    },
                    strictBounds: true
                },
                maxZoom: this.MAX_ZOOM,
                minZoom: this.MIN_ZOOM
            }))
        );

        this.apiLoaded$ = this.googleMapsSandBox.publicLoadApi();
    }

    ngOnInit(): void {
        this.initMarkers();
    }

    ngAfterViewInit(): void {
        this.initMarkerListener();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.actionGroups$.complete();
        this.records$.complete();
        this.clusters$.complete();
    }

    private initMarkers(): void {
        this.markers$ = combineLatest([this.records$, this.actionGroups$]).pipe(
            debounceTime(10),
            map(([records, actionGroups]: [PartialRecord[], ActionGroup[]]) =>
                records
                    .map((record, index) => this.recordToMarker(record, actionGroups[index]))
                    .filter((marker): marker is Marker<PartialRecord> => !!marker)
            ),
            distinctUntilChanged(isEqual),
            shareReplay(1)
        );
    }

    private recordToMarker(record: PartialRecord, actionGroup: ActionGroup): Marker<PartialRecord> | null {
        let coordinate: Coordinate | null = this.positionFieldDefinition
            ? this.fieldReadHelper.getPositionFieldCoordinateValue(this.positionFieldDefinition, record)
            : record.getLocation();

        let address: string | null = this.positionFieldDefinition
            ? this.fieldReadHelper.getPositionFieldAddressValue(this.positionFieldDefinition, record)
            : record.getFormattedAddress();

        coordinate ??= new Coordinate(0, 0);

        const position: google.maps.LatLngLiteral = {
            lat: coordinate.latitude,
            lng: coordinate.longitude
        };

        if (!address) {
            address = '';
        }

        if (actionGroup) {
            // Dropdowns do not work properly in the map
            // Actions also were not officially in scope for MED-1178, so filtering them out for now
            const actions: ActionItem[] =
                actionGroup?.actions
                    .filter(action => !action.actionItems && !action.asyncActionItems)
                    .map(action => ({
                        ...action,
                        modifiers: action.modifiers
                            ? [...action.modifiers, ActionItemModifier.Small]
                            : [ActionItemModifier.Small]
                    })) ?? [];

            actionGroup = {
                ...actionGroup,
                actions,
                modifiers: [ActionGroupModifier.End, ActionGroupModifier.Condense]
            };
        }

        return {
            id: record.getFragmentId(),
            title: record.getTitle(),
            imgSrc: record.getThumbnailUrl(),
            coordinate,
            position,
            address,
            navigationLink: {
                route: record.getDetailUrl()
            },
            actionGroup,
            data: record
        };
    }

    private initMarkerListener(): void {
        combineLatest([this.markers$, this.googleMapIsReady$.pipe(distinctUntilChanged())])
            .pipe(
                debounceTime(10),
                filter(([, googleMapReady]) => googleMapReady && this.autoFitBounds),
                takeUntil(this.destroy$)
            )
            .subscribe(([markers]) => {
                this.initBoundsListener();
                this.setMapBounds(markers);
            });
    }

    private initBoundsListener(): void {
        this.googleMap.boundsChanged
            .pipe(
                debounceTime(500),
                distinctUntilChanged(isEqual),
                takeUntil(this.destroy$),
                filter(bounds => !!bounds)
            )
            .subscribe((bounds: google.maps.LatLngBounds) => {
                this.boundsChange.next(bounds);
            });
    }

    // #region Map manipulation helpers
    private setMapBounds(markers: Marker<PartialRecord>[]): void {
        const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();

        if (markers.length === 0) {
            return;
        }

        for (const marker of markers) {
            bounds.extend(marker.coordinate.toLatLing());
        }
        this.googleMap.fitBounds(bounds);
    }

    private zoomOnCluster(clusterMarkers: google.maps.marker.AdvancedMarkerElement[]): void {
        const latlngbounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();

        clusterMarkers.forEach((marker: google.maps.marker.AdvancedMarkerElement) => {
            const position:
                google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral | null | undefined =
                marker.position;
            if (position) {
                latlngbounds.extend(position);
            }
        });

        this.googleMap.fitBounds(latlngbounds);

        // Pan to bounds before panning to the center: GMaps will undershoot and end up below target otherwise.
        // This gets worse the further away from the equator the cluster is.
        this.googleMap.panToBounds(latlngbounds);
        this.googleMap.panTo(latlngbounds.getCenter());
    }
    // #endregion

    // #region InfoWindow helpers
    public openClusterInfoDialog(
        googleClusterMarkers: google.maps.marker.AdvancedMarkerElement[],
        allMarkers: Marker<PartialRecord>[]
    ): void {
        const clusterMarkerIds: Set<string> = new Set(
            googleClusterMarkers.map(marker => marker.title).filter((id): id is string => !!id)
        );

        const clusterMarkers: Marker<PartialRecord>[] = allMarkers.filter(marker => clusterMarkerIds.has(marker.id));

        const config: DialogConfig = {
            data: {
                clusterMarkers
            }
        };
        this.dialogService.open(ShClusterInfoDialogComponent, config);
    }
    // #endregion

    // #region Event handlers
    public openInfo(marker: MapAnchorPoint, content: Marker<PartialRecord>): void {
        this.infoContent = content;
        this.info.open(marker);
    }

    public onClusterClick(cluster: Cluster, allMarkers: Marker<PartialRecord>[]): void {
        const clusterMarkers: google.maps.marker.AdvancedMarkerElement[] =
            cluster.markers as google.maps.marker.AdvancedMarkerElement[];

        this.info.close();

        const position: google.maps.LatLngLiteral = clusterMarkers[0].position as google.maps.LatLngLiteral;
        const allSamePosition = clusterMarkers.every(
            marker => marker.position?.lat === position?.lat && marker.position?.lng === position?.lng
        );

        if (allSamePosition) {
            if (position) {
                const latlng: google.maps.LatLng = new google.maps.LatLng(position.lat, position.lng);

                // panTo only works inside a timeOut.
                // this.googleMap.googleMap.setCenter also works outside a timeout, but snaps into place.
                setTimeout(() => {
                    this.googleMap.panTo(latlng);
                    this.googleMap.googleMap!.setZoom(
                        Math.max(this.DEFAULT_ZOOM, this.googleMap.googleMap!.getZoom() ?? 0)
                    );
                });
            }
            this.openClusterInfoDialog(clusterMarkers, allMarkers);
        } else {
            this.zoomOnCluster(clusterMarkers);
        }
    }

    public onClusterChanges(clusters: Cluster[]): void {
        this.clusters$.next(clusters);
    }
    // #endregion

    public trackById(index: number, marker: Marker<PartialRecord>): string {
        return marker.id;
    }
}
